import { useState, useEffect, useRef } from 'react';
import { FaPlay, FaExternalLinkAlt, FaCheckCircle, FaUsers, FaStar, FaCrown, FaLink, FaEnvelope, FaPhone, FaChevronLeft, FaChevronRight, FaSpinner, FaHeart, FaAward, FaMicrophone } from 'react-icons/fa';
import { MdBusiness, MdTrendingUp, MdPerson, MdGroups } from 'react-icons/md';
import axios from 'axios';
import ReCAPTCHA from "react-google-recaptcha";
import Footer from '../components/Footer';

function MyPowerBrandPage() {
    // API URLs
    const API_URL = `${process.env.REACT_APP_API_BASE_URL}/UImyPowerBrand`;
    const API_MEDIA = `${process.env.REACT_APP_API_BASE_URL}/UImedia`;
    const NOMINATION_API = `${process.env.REACT_APP_API_BASE_URL}/myPowerBrandNominations`;

    // State for dynamic data
    const [powerBrandData, setPowerBrandData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        nomineeName: '',
        currentRole: '',
        organization: '',
        areasOfImpact: [],
        storyDescription: '',
        relevantLinks: '',
        email: '',
        phone: '',
        declarationAccepted: false
    });

    // Form submission state
    const [recaptchaToken, setRecaptchaToken] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const recaptchaRef = useRef(null);

    // Slider state
    const [currentSlide, setCurrentSlide] = useState(0);
    const [itemsPerView, setItemsPerView] = useState(4);
    const sliderRef = useRef(null);

    // Icon mapping
    const iconComponents = {
        trending: <MdTrendingUp className="text-3xl" />,
        star: <FaStar className="text-3xl" />,
        crown: <FaCrown className="text-3xl" />,
        business: <MdBusiness />,
        groups: <MdGroups />,
        person: <MdPerson />,
        heart: <FaHeart className="text-3xl" />,
        award: <FaAward className="text-3xl" />,
        microphone: <FaMicrophone className="text-3xl" />
    };

    // Static form fields
    const formFields = {
        areasOfImpact: [
            { value: "entrepreneurship", label: "Entrepreneurship" },
            { value: "businessLeadership", label: "Business Leadership" },
            { value: "professionalExcellence", label: "Professional Excellence" },
            { value: "socialImpact", label: "Social Impact" },
            { value: "other", label: "Other" }
        ],
        declaration: "I acknowledge that nomination for the My Power Brand Podcast is entirely free and does not guarantee selection. I further understand that My Power Brand is a professionally produced, high-quality podcast, and I am willing to share a portion of the production cost if shortlisted."
    };

    // Fetch data from API

    useEffect(() => {
        const fetchPowerBrandData = async () => {
            try {
                setLoading(true);
                const response = await axios.get(API_URL);


                const data = response.data.data || response.data;

                if (!data || data.length === 0) {
                    throw new Error('No MyPowerBrand data found');
                }


                let entry;
                if (Array.isArray(data)) {

                    const activeEntries = data.filter(item => item.isActive === true);

                    if (activeEntries.length === 0) {
                        throw new Error('No active MyPowerBrand entry found');
                    }


                    entry = activeEntries[0];
                } else {

                    if (!data.isActive) {
                        throw new Error('MyPowerBrand entry is not active');
                    }
                    entry = data;
                }

                // Load images
                const entryWithImages = await loadImages(entry);

                setPowerBrandData(entryWithImages);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching MyPowerBrand data:', err);
                setError(err.message);
                setLoading(false);
            }
        };

        fetchPowerBrandData();
    }, [API_URL]);

    // Load images for the entry
    const loadImages = async (entry) => {
        try {
            // Load hero background image
            let heroBackgroundUrl = null;
            if (entry.heroSection?.backgroundImage?._id || entry.heroSection?.backgroundImage?.id) {
                try {
                    const imageId = entry.heroSection.backgroundImage._id || entry.heroSection.backgroundImage.id;
                    const imgRes = await axios.get(
                        `${API_MEDIA}/${imageId}/download`,
                        { responseType: "blob" }
                    );
                    heroBackgroundUrl = URL.createObjectURL(imgRes.data);
                } catch (error) {
                    console.error("Error loading hero background:", error);
                }
            }

            // Load core values icons
            const coreValuesWithImages = await Promise.all(
                (entry.coreValues || []).map(async (value) => {
                    if (value.icon?._id || value.icon?.id) {
                        try {
                            const iconId = value.icon._id || value.icon.id;
                            const iconRes = await axios.get(
                                `${API_MEDIA}/${iconId}/download`,
                                { responseType: "blob" }
                            );
                            const imageUrl = URL.createObjectURL(iconRes.data);
                            return { ...value, iconUrl: imageUrl, iconId };
                        } catch (error) {
                            console.error("Error loading core value icon:", error);
                            return { ...value, iconUrl: null };
                        }
                    }
                    return { ...value, iconUrl: null };
                })
            );

            // Load podcast thumbnails
            const podcastEpisodesWithImages = await Promise.all(
                (entry.podcastEpisodes || []).map(async (episode) => {
                    if (episode.thumbnail?._id || episode.thumbnail?.id) {
                        try {
                            const thumbnailId = episode.thumbnail._id || episode.thumbnail.id;
                            const thumbRes = await axios.get(
                                `${API_MEDIA}/${thumbnailId}/download`,
                                { responseType: "blob" }
                            );
                            const imageUrl = URL.createObjectURL(thumbRes.data);
                            return { ...episode, thumbnailUrl: imageUrl };
                        } catch (error) {
                            console.error("Error loading podcast thumbnail:", error);
                            return { ...episode, thumbnailUrl: null };
                        }
                    }
                    return episode;
                })
            );

            // Load eligible candidates icons
            const eligibleCandidatesWithImages = await Promise.all(
                (entry.eligibleCandidates || []).map(async (candidate) => {
                    if (candidate.icon?._id || candidate.icon?.id) {
                        try {
                            const iconId = candidate.icon._id || candidate.icon.id;
                            const iconRes = await axios.get(
                                `${API_MEDIA}/${iconId}/download`,
                                { responseType: "blob" }
                            );
                            const imageUrl = URL.createObjectURL(iconRes.data);
                            return { ...candidate, iconUrl: imageUrl };
                        } catch (error) {
                            console.error("Error loading candidate icon:", error);
                            return { ...candidate, iconUrl: null };
                        }
                    }
                    return candidate;
                })
            );

            // Load process steps icons
            const processStepsWithImages = await Promise.all(
                (entry.processSteps || []).map(async (step) => {
                    if (step.icon?._id || step.icon?.id) {
                        try {
                            const iconId = step.icon._id || step.icon.id;
                            const iconRes = await axios.get(
                                `${API_MEDIA}/${iconId}/download`,
                                { responseType: "blob" }
                            );
                            const imageUrl = URL.createObjectURL(iconRes.data);
                            return { ...step, iconUrl: imageUrl };
                        } catch (error) {
                            console.error("Error loading process step icon:", error);
                            return { ...step, iconUrl: null };
                        }
                    }
                    return step;
                })
            );

            // Load benefits icons
            const benefitsWithImages = await Promise.all(
                (entry.benefits || []).map(async (benefit) => {
                    if (benefit.icon?._id || benefit.icon?.id) {
                        try {
                            const iconId = benefit.icon._id || benefit.icon.id;
                            const iconRes = await axios.get(
                                `${API_MEDIA}/${iconId}/download`,
                                { responseType: "blob" }
                            );
                            const imageUrl = URL.createObjectURL(iconRes.data);
                            return { ...benefit, iconUrl: imageUrl };
                        } catch (error) {
                            console.error("Error loading benefit icon:", error);
                            return { ...benefit, iconUrl: null };
                        }
                    }
                    return benefit;
                })
            );

            return {
                ...entry,
                heroBackgroundUrl,
                coreValues: coreValuesWithImages,
                podcastEpisodes: podcastEpisodesWithImages,
                eligibleCandidates: eligibleCandidatesWithImages,
                processSteps: processStepsWithImages,
                benefits: benefitsWithImages
            };

        } catch (error) {
            console.error("Error loading images:", error);
            return entry;
        }
    };

    // Cleanup image URLs
    useEffect(() => {
        return () => {
            if (powerBrandData) {
                if (powerBrandData.heroBackgroundUrl) {
                    URL.revokeObjectURL(powerBrandData.heroBackgroundUrl);
                }
                powerBrandData.coreValues?.forEach(value => {
                    if (value.iconUrl) URL.revokeObjectURL(value.iconUrl);
                });
                powerBrandData.podcastEpisodes?.forEach(episode => {
                    if (episode.thumbnailUrl) URL.revokeObjectURL(episode.thumbnailUrl);
                });
                powerBrandData.eligibleCandidates?.forEach(candidate => {
                    if (candidate.iconUrl) URL.revokeObjectURL(candidate.iconUrl);
                });
                powerBrandData.processSteps?.forEach(step => {
                    if (step.iconUrl) URL.revokeObjectURL(step.iconUrl);
                });
                powerBrandData.benefits?.forEach(benefit => {
                    if (benefit.iconUrl) URL.revokeObjectURL(benefit.iconUrl);
                });
            }
        };
    }, [powerBrandData]);

    // Handle responsive items per view
    useEffect(() => {
        const updateItemsPerView = () => {
            if (window.innerWidth < 768) {
                setItemsPerView(1);
            } else if (window.innerWidth < 1024) {
                setItemsPerView(2);
            } else {
                setItemsPerView(4);
            }
        };

        updateItemsPerView();
        window.addEventListener('resize', updateItemsPerView);
        return () => window.removeEventListener('resize', updateItemsPerView);
    }, []);

    // Calculate total slides
    const totalSlides = powerBrandData
        ? Math.ceil(powerBrandData.podcastEpisodes?.length / itemsPerView)
        : 0;

    // Navigation functions
    const nextSlide = () => {
        setCurrentSlide(prev => (prev + 1) % totalSlides);
    };

    const prevSlide = () => {
        setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
    };

    const goToSlide = (index) => {
        setCurrentSlide(index);
    };

    // Auto slide for episodes slider
    useEffect(() => {
        if (!powerBrandData || powerBrandData.podcastEpisodes?.length <= itemsPerView) return;

        const interval = setInterval(() => {
            nextSlide();
        }, 5000);

        return () => clearInterval(interval);
    }, [currentSlide, itemsPerView, powerBrandData]);

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === 'checkbox') {
            if (name === 'areasOfImpact') {
                const updatedAreas = formData.areasOfImpact.includes(value)
                    ? formData.areasOfImpact.filter(area => area !== value)
                    : [...formData.areasOfImpact, value];
                setFormData({ ...formData, areasOfImpact: updatedAreas });
            } else {
                setFormData({ ...formData, [name]: checked });
            }
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    // reCAPTCHA handler
    const handleRecaptchaChange = (token) => {
        setRecaptchaToken(token);
        setSubmitError(''); // Clear any previous errors
    };

    // Reset reCAPTCHA
    const resetRecaptcha = () => {
        setRecaptchaToken('');
        if (recaptchaRef.current) {
            recaptchaRef.current.reset();
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            nomineeName: '',
            currentRole: '',
            organization: '',
            areasOfImpact: [],
            storyDescription: '',
            relevantLinks: '',
            email: '',
            phone: '',
            declarationAccepted: false
        });
        resetRecaptcha();
    };

    // Form validation
    const validateForm = () => {
        if (!formData.nomineeName.trim()) {
            return "Nominee name is required";
        }
        if (!formData.currentRole.trim()) {
            return "Current role is required";
        }
        if (!formData.organization.trim()) {
            return "Organization name is required";
        }
        if (formData.areasOfImpact.length === 0) {
            return "Please select at least one area of impact";
        }
        if (!formData.storyDescription.trim()) {
            return "Story description is required";
        }
        if (!formData.email.trim()) {
            return "Email is required";
        }
        if (!formData.phone.trim()) {
            return "Phone number is required";
        }
        if (!formData.declarationAccepted) {
            return "You must accept the declaration";
        }

        return null;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Clear previous messages
        setSubmitSuccess(false);
        setSubmitError('');

        // Validate form
        const validationError = validateForm();
        if (validationError) {
            setSubmitError(validationError);
            return;
        }

        try {
            setIsSubmitting(true);

            // Prepare submission data
            const submissionData = {
                ...formData,
                recaptchaToken
            };

            // Submit to backend
            const response = await axios.post(NOMINATION_API, submissionData);

            if (response.data.success) {
                // Show success message
                setSubmitSuccess(true);

                // Reset form
                resetForm();

                // Auto hide success message after 5 seconds
                setTimeout(() => {
                    setSubmitSuccess(false);
                }, 5000);
            } else {
                setSubmitError(response.data.message || 'Submission failed. Please try again.');
                resetRecaptcha();
            }
        } catch (err) {
            console.error('Submission error:', err);

            // Handle specific errors
            if (err.response) {
                if (err.response.status === 400) {
                    setSubmitError(err.response.data.message || 'Invalid form data. Please check your inputs.');
                } else if (err.response.status === 500) {
                    setSubmitError('Server error. Please try again later.');
                } else {
                    setSubmitError('An error occurred. Please try again.');
                }
            } else {
                setSubmitError('Network error. Please check your connection and try again.');
            }

            resetRecaptcha();
        } finally {
            setIsSubmitting(false);
        }
    };

    const openEpisode = (url) => {
        window.open(url, '_blank');
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-700 flex items-center justify-center">
                <div className="text-center">
                    <FaSpinner className="animate-spin text-amber-500 text-4xl mx-auto mb-4" />
                    <p className="text-amber-200 text-lg">Loading My Power Brand...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !powerBrandData) {
        return (
            <div className="min-h-screen bg-gray-700 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-red-400 text-2xl mb-4">⚠️</div>
                    <p className="text-amber-200 text-lg">Unable to load My Power Brand data</p>
                    <p className="text-gray-400 mt-2">Please try again later</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-700 text-white">
            {/* Hero Section with Background Image */}
            <header className="relative py-20 px-4">
                {/* Background Image */}
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: powerBrandData.heroBackgroundUrl
                            ? `url(${powerBrandData.heroBackgroundUrl})`
                            : 'linear-gradient(135deg, #ffffff 0%, #0f172a 100%)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                    }}
                />

                {/* Dark overlay for text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/30"></div>

                <div className="relative max-w-6xl mx-auto text-center">
                    <div className="inline-block bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900 font-bold px-6 py-2 rounded-full mb-6">
                        {powerBrandData.heroSection?.initiative || "A MASA Forum Initiative"}
                    </div>
                    <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
                        {powerBrandData.heroSection?.title || "My Power Brand"}
                    </h1>
                    <p className="text-2xl md:text-3xl font-light mb-8 text-amber-100">
                        {powerBrandData.heroSection?.tagline || "Voices that Empower. Stories that Inspire."}
                    </p>
                    <div className="max-w-3xl mx-auto">
                        <p className="text-xl text-white leading-relaxed">
                            {powerBrandData.heroSection?.description || "My Power Brand is a distinguished podcast initiative by MASA Forum, created to spotlight individuals whose journeys reflect leadership, resilience, and meaningful impact."}
                        </p>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 py-12">
                {/* About Section */}
                <section className="mb-16">
                    <div className="bg-gradient-to-br bg-gray-900 to-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-amber-500/20">
                        <h2 className="text-3xl font-bold mb-6 flex items-center gap-3 text-amber-300">
                            <MdBusiness className="text-amber-400" />
                            {powerBrandData.aboutSection?.title || "ABOUT MY POWER BRAND"}
                        </h2>
                        <div className="space-y-6">
                            <div className="bg-gray-700/30 p-6 rounded-xl border-l-4 border-amber-500">
                                <p className="text-xl italic text-amber-100 mb-4">
                                    "{powerBrandData.aboutSection?.quote || "My Power Brand is not a promotional showcase."}"
                                </p>
                                <p className="text-lg text-amber-50">
                                    {powerBrandData.aboutSection?.description || "It is a carefully curated platform dedicated to documenting stories that define credibility and character."}
                                </p>
                            </div>
                            <p className="text-lg text-amber-100 leading-relaxed">
                                {powerBrandData.aboutSection?.detailedDescription || "The podcast features voices that have shaped industries, inspired communities, and demonstrated excellence through consistent action, ethical leadership, and tangible outcomes — beyond trends or temporary recognition."}
                            </p>
                        </div>
                    </div>
                </section>

                {/* Core Values */}
                {powerBrandData.coreValues && powerBrandData.coreValues.length > 0 && (
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold mb-10 text-center text-amber-300">
                            WHY <span className="text-amber-400">MY POWER BRAND</span>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {powerBrandData.coreValues.map((value, index) => (
                                <div key={index} className="bg-gradient-to-br bg-gray-900 to-gray-800/50 p-6 rounded-xl border border-amber-500/20 hover:border-amber-500/40 transition-all group hover:scale-105">
                                    <div className="text-amber-400 mb-4">
                                        {value.iconUrl ? (
                                            <img
                                                src={value.iconUrl}
                                                alt={value.title}
                                                className="w-12 h-12 object-contain"
                                            />
                                        ) : (
                                            iconComponents[value.icon] || <FaStar className="text-3xl" />
                                        )}
                                    </div>
                                    <h3 className="text-xl font-bold mb-3 text-amber-200">{value.title}</h3>
                                    <p className="text-amber-100">{value.description}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Featured Episodes with Slider */}
                {powerBrandData.podcastEpisodes && powerBrandData.podcastEpisodes.length > 0 && (
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold mb-10 flex items-center gap-3 text-amber-300">
                            <FaPlay className="text-amber-400" />
                            FEATURED POWER BRAND VOICES
                        </h2>

                        <div className="relative">
                            {/* Slider Container */}
                            <div ref={sliderRef} className="overflow-hidden">
                                <div
                                    className="flex transition-transform duration-500 ease-in-out"
                                    style={{
                                        transform: `translateX(-${currentSlide * (100 / itemsPerView)}%)`,
                                        width: `${100 * totalSlides}%`
                                    }}
                                >
                                    {powerBrandData.podcastEpisodes.map((episode, index) => (
                                        <div
                                            key={index}
                                            className="p-2"
                                            style={{ width: `${100 / itemsPerView}%` }}
                                        >
                                            <div className="bg-gradient-to-b bg-gray-900 to-gray-800/50 rounded-xl overflow-hidden border border-gray-600/50 hover:border-amber-500/40 transition-all group h-full">
                                                <div
                                                    className="h-48 bg-cover bg-center relative cursor-pointer"
                                                    style={{
                                                        backgroundImage: episode.thumbnailUrl
                                                            ? `url(${episode.thumbnailUrl})`
                                                            : 'linear-gradient(135deg, #374151 0%, #1f2937 100%)'
                                                    }}
                                                    onClick={() => openEpisode(episode.url)}
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                                                    <div className="absolute inset-0 flex items-center justify-center">
                                                        <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-yellow-500/80 rounded-full flex items-center justify-center hover:from-amber-600 hover:to-yellow-600 transition-all transform group-hover:scale-110">
                                                            <FaPlay className="text-white text-xl ml-1" />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="p-5">
                                                    <h3 className="font-bold text-lg mb-2 line-clamp-2 text-white">{episode.title}</h3>
                                                    <p className="text-amber-200 text-sm mb-4">{episode.guest}</p>
                                                    <button
                                                        onClick={() => openEpisode(episode.url)}
                                                        className="flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors text-sm"
                                                    >
                                                        <FaExternalLinkAlt /> Watch Episode
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Navigation Buttons */}
                            {powerBrandData.podcastEpisodes.length > itemsPerView && (
                                <>
                                    <button
                                        onClick={prevSlide}
                                        className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-12 h-12 bg-gray-800/80 hover:bg-gray-900 text-amber-400 rounded-full flex items-center justify-center transition-all hover:scale-110 z-10 border border-amber-500/30"
                                        aria-label="Previous episodes"
                                    >
                                        <FaChevronLeft />
                                    </button>
                                    <button
                                        onClick={nextSlide}
                                        className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-1/2 w-12 h-12 bg-gray-800/80 hover:bg-gray-900 text-amber-400 rounded-full flex items-center justify-center transition-all hover:scale-110 z-10 border border-amber-500/30"
                                        aria-label="Next episodes"
                                    >
                                        <FaChevronRight />
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Slider Indicators */}
                        {totalSlides > 1 && (
                            <div className="flex justify-center gap-2 mt-8">
                                {Array.from({ length: totalSlides }).map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => goToSlide(index)}
                                        className={`w-3 h-3 rounded-full transition-all ${index === currentSlide
                                            ? 'bg-gradient-to-r from-amber-500 to-yellow-500 scale-125'
                                            : 'bg-gray-600 hover:bg-gray-500'
                                            }`}
                                        aria-label={`Go to slide ${index + 1}`}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Episode Counter */}
                        <div className="text-center mt-4 text-amber-300 text-sm">
                            Showing {Math.min(currentSlide * itemsPerView + 1, powerBrandData.podcastEpisodes.length)}-
                            {Math.min((currentSlide + 1) * itemsPerView, powerBrandData.podcastEpisodes.length)}
                            of {powerBrandData.podcastEpisodes.length} episodes
                        </div>
                    </section>
                )}

                {/* Who May Apply */}
                {powerBrandData.eligibleCandidates && powerBrandData.eligibleCandidates.length > 0 && (
                    <section className="mb-16">
                        <div className="bg-gradient-to-br bg-gray-900 to-gray-900/50 p-8 rounded-2xl border border-amber-500/20">
                            <h2 className="text-3xl font-bold mb-8 flex items-center gap-3 text-amber-300">
                                <FaUsers className="text-amber-400" />
                                WHO MAY APPLY OR BE NOMINATED
                            </h2>
                            <p className="text-lg text-amber-100 mb-8">
                                My Power Brand invites nominations of individuals who have demonstrated exceptional contribution and influence in their respective domains, including:
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {powerBrandData.eligibleCandidates.map((candidate, index) => (
                                    <div key={index} className="flex items-start gap-3 p-4 bg-gray-700/30 rounded-lg">
                                        <div className="text-amber-400 mt-1">
                                            {candidate.iconUrl ? (
                                                <img
                                                    src={candidate.iconUrl}
                                                    alt="Candidate icon"
                                                    className="w-6 h-6 object-cover"
                                                />
                                            ) : (
                                                iconComponents[candidate.icon] || <FaCheckCircle />
                                            )}
                                        </div>
                                        <p className="text-amber-100">{candidate.text}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* ✅ CHANGED SECTION: Process & Benefits - VERTICAL LAYOUT (ONE BELOW OTHER) */}
                <section className="mb-16">
                    {powerBrandData.processSteps && powerBrandData.processSteps.length > 0 && (
                        <div className="bg-gradient-to-br bg-gray-900 to-gray-900/50 p-8 rounded-2xl border border-amber-500/20 mb-8">
                            <h3 className="text-2xl font-bold mb-8 text-amber-300 text-center">What Happens After You Apply</h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {powerBrandData.processSteps.map((step, index) => (
                                    <div key={index} className="flex flex-col items-center text-center bg-gray-700/30 p-6 rounded-xl border border-amber-500/20 hover:border-amber-500/40 transition-all">
                                        {/* Step Number - TOP */}
                                        <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full flex items-center justify-center mb-4 shadow-lg">
                                            <span className="text-lg font-bold text-gray-900">{step.step}</span>
                                        </div>
                                        {step.iconUrl && (
                                            <div className="mt-2">
                                                <img
                                                    src={step.iconUrl}
                                                    alt={`Step ${step.step} icon`}
                                                    className="w-16 h-16 object-contain rounded-lg border border-amber-200"
                                                />
                                            </div>
                                        )}

                                        {/* Description - MIDDLE */}
                                        <div className="mb-4">
                                            <p className="text-amber-100 text-lg leading-relaxed">{step.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {powerBrandData.benefits && powerBrandData.benefits.length > 0 && (
                        <div className="bg-gradient-to-br bg-gray-900 to-gray-900/50 p-8 rounded-2xl border border-amber-500/20">
                            <h3 className="text-2xl font-bold mb-8 text-amber-300 text-center">Why Be Featured on My Power Brand</h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {powerBrandData.benefits.map((benefit, index) => (
                                    <div key={index} className="flex flex-col items-center text-center bg-gray-700/30 p-6 rounded-xl border border-amber-500/20 hover:border-amber-500/40 transition-all">
                                        {/* Icon - TOP */}
                                        <div className="mb-4">
                                            {benefit.iconUrl ? (
                                                <img
                                                    src={benefit.iconUrl}
                                                    alt="Benefit icon"
                                                    className="w-12 h-12 object-cover mx-auto"
                                                />
                                            ) : (
                                                <FaCheckCircle className="text-green-400 text-3xl mx-auto" />
                                            )}
                                        </div>

                                        {/* Text - BOTTOM */}
                                        <div>
                                            <p className="text-amber-100 text-lg leading-relaxed">{benefit.text}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </section>

                {/* Nomination Form */}
                <section>
                    <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-8 border border-amber-500/30">
                        <h2 className="text-3xl font-bold mb-2 text-center text-amber-300">
                            NOMINATION FOR MY POWER BRAND PODCAST
                        </h2>
                        <p className="text-amber-200 text-center mb-8">
                            Submit your nomination for individuals whose stories deserve to be heard
                        </p>



                        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
                            <div className="space-y-6">
                                {/* Name */}
                                <div>
                                    <label className="block text-amber-300 mb-2 font-medium">
                                        Name of the Nominee *
                                    </label>
                                    <input
                                        type="text"
                                        name="nomineeName"
                                        value={formData.nomineeName}
                                        onChange={handleInputChange}
                                        required
                                        disabled={isSubmitting}
                                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        placeholder="Enter nominee's full name"
                                    />
                                </div>

                                {/* Role & Organization */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-amber-300 mb-2 font-medium">
                                            Current Role / Designation *
                                        </label>
                                        <input
                                            type="text"
                                            name="currentRole"
                                            value={formData.currentRole}
                                            onChange={handleInputChange}
                                            required
                                            disabled={isSubmitting}
                                            className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition disabled:opacity-50 disabled:cursor-not-allowed"
                                            placeholder="e.g., CEO, Founder, Director"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-amber-300 mb-2 font-medium">
                                            Organization / Enterprise Name *
                                        </label>
                                        <input
                                            type="text"
                                            name="organization"
                                            value={formData.organization}
                                            onChange={handleInputChange}
                                            required
                                            disabled={isSubmitting}
                                            className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition disabled:opacity-50 disabled:cursor-not-allowed"
                                            placeholder="Company or organization name"
                                        />
                                    </div>
                                </div>

                                {/* Areas of Impact */}
                                <div>
                                    <label className="block text-amber-300 mb-2 font-medium">
                                        Primary Area of Impact *
                                    </label>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        {formFields.areasOfImpact.map((area) => (
                                            <label
                                                key={area.value}
                                                className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-colors ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-700/50'} ${formData.areasOfImpact.includes(area.value) ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-gray-700/30'}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="areasOfImpact"
                                                    value={area.value}
                                                    checked={formData.areasOfImpact.includes(area.value)}
                                                    onChange={handleInputChange}
                                                    disabled={isSubmitting}
                                                    className="text-amber-500 focus:ring-amber-500 disabled:cursor-not-allowed"
                                                />
                                                <span className="text-amber-100">{area.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Story Description */}
                                <div>
                                    <label className="block text-amber-300 mb-2 font-medium">
                                        Briefly describe why this story deserves to be featured *
                                    </label>
                                    <textarea
                                        name="storyDescription"
                                        value={formData.storyDescription}
                                        onChange={handleInputChange}
                                        required
                                        rows="3"
                                        disabled={isSubmitting}
                                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        placeholder="Share the compelling aspects of their journey (3-4 lines)"
                                    />
                                </div>

                                {/* Relevant Links */}
                                <div>
                                    <label className="block text-amber-300 mb-2 font-medium flex items-center gap-2">
                                        <FaLink />
                                        Relevant links showcasing work or achievements
                                    </label>
                                    <input
                                        type="url"
                                        name="relevantLinks"
                                        value={formData.relevantLinks}
                                        onChange={handleInputChange}
                                        disabled={isSubmitting}
                                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        placeholder="LinkedIn profile / website / media coverage URLs"
                                    />
                                </div>

                                {/* Contact Information */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-amber-300 mb-2 font-medium flex items-center gap-2">
                                            <FaEnvelope />
                                            Email Address *
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            disabled={isSubmitting}
                                            className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition disabled:opacity-50 disabled:cursor-not-allowed"
                                            placeholder="Your email address"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-amber-300 mb-2 font-medium flex items-center gap-2">
                                            <FaPhone />
                                            Phone Number *
                                        </label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            required
                                            disabled={isSubmitting}
                                            className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition disabled:opacity-50 disabled:cursor-not-allowed"
                                            placeholder="Your phone number"
                                        />
                                    </div>
                                </div>

                                {/* Declaration */}
                                <div className={`bg-gray-700/30 p-6 rounded-lg border ${formData.declarationAccepted ? 'border-amber-500/50' : 'border-gray-600'}`}>
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="declarationAccepted"
                                            checked={formData.declarationAccepted}
                                            onChange={handleInputChange}
                                            required
                                            disabled={isSubmitting}
                                            className="mt-1 text-amber-500 focus:ring-amber-500 disabled:cursor-not-allowed"
                                        />
                                        <div className="text-amber-100">
                                            <p className="font-medium mb-2">Mandatory Declaration:</p>
                                            <p className="text-sm">
                                                {formFields.declaration}
                                            </p>
                                        </div>
                                    </label>
                                </div>

                                {/* reCAPTCHA */}
                                <div className="mb-6">
                                    <div className="flex justify-center">
                                        <ReCAPTCHA
                                            ref={recaptchaRef}
                                            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                            onChange={handleRecaptchaChange}
                                            theme="dark"
                                            size="normal"
                                        />
                                    </div>

                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isSubmitting || !recaptchaToken}
                                    className={`w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900 font-bold py-4 px-6 rounded-lg transition-all transform focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-gray-800 ${isSubmitting || !recaptchaToken
                                            ? 'opacity-50 cursor-not-allowed'
                                            : 'hover:from-amber-600 hover:to-yellow-600 hover:scale-105'
                                        }`}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <FaSpinner className="animate-spin inline-block mr-2" />
                                            SUBMITTING...
                                        </>
                                    ) : (
                                        'SUBMIT NOMINATION'
                                    )}
                                </button>
                                {/* Success Message */}
                                {submitSuccess && (
                                    <div className="mb-6 p-4 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-lg">
                                        <div className="flex items-center gap-3 text-green-400">
                                            <FaCheckCircle className="text-xl" />
                                            <div>
                                                <p className="font-bold">Nomination Submitted Successfully!</p>
                                                <p className="text-green-300 text-sm mt-1">
                                                    Thank you for your nomination. Our team will review the submission and contact you if shortlisted.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Error Message */}
                                {submitError && (
                                    <div className="mb-6 p-4 bg-gradient-to-r from-red-500/20 to-rose-500/20 border border-red-500/30 rounded-lg">
                                        <div className="flex items-center gap-3 text-red-400">
                                            <div className="text-xl">⚠️</div>
                                            <div>
                                                <p className="font-bold">Submission Error</p>
                                                <p className="text-red-300 text-sm mt-1">{submitError}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Form Note */}
                                <p className="text-center text-amber-200/60 text-sm mt-4">
                                    * All fields marked with asterisk are required
                                </p>
                            </div>
                        </form>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}

export default MyPowerBrandPage;